/**
 * RulesTab - Display view rules in read-only format
 *
 * Shows all configured file selection rules with their settings
 */

import React from 'react';
import { Filter, Calendar, FileType, Database, Tag as TagIcon, HardDrive } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { View, RuleConfig } from '@/types/views';

interface RulesTabProps {
  view: View;
}

/**
 * Get rule type badge color
 */
const getRuleTypeBadge = (type: string) => {
  const colors: Record<string, string> = {
    path_pattern: 'bg-info/10 text-info border-info/30',
    source_id: 'bg-violet/10 text-violet border-violet/30',
    dataset_id: 'bg-success/10 text-success border-success/30',
    metadata: 'bg-warning/10 text-warning border-warning/30',
    date_range: 'bg-rose/10 text-rose border-rose/30',
    file_size: 'bg-alert/10 text-alert border-alert/30',
    tag: 'bg-teal/10 text-teal border-teal/30',
  };

  return colors[type] || 'bg-muted text-muted-foreground border-border';
};

/**
 * Get rule type icon
 */
const getRuleIcon = (type: string) => {
  const icons: Record<string, any> = {
    path_pattern: FileType,
    source_id: Database,
    dataset_id: Database,
    metadata: Filter,
    date_range: Calendar,
    file_size: HardDrive,
    tag: TagIcon,
  };

  return icons[type] || Filter;
};

/**
 * Get rule type label
 */
const getRuleTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    path_pattern: 'Path Pattern',
    source_id: 'Source ID',
    dataset_id: 'Dataset ID',
    metadata: 'Metadata',
    date_range: 'Date Range',
    file_size: 'File Size',
    tag: 'Tag',
  };

  return labels[type] || type;
};

/**
 * Render rule configuration details
 */
const RuleConfigDetails: React.FC<{ config: RuleConfig }> = ({ config }) => {
  switch (config.type) {
    case 'path_pattern':
      return (
        <div className="space-y-2">
          <div>
            <div className="text-sm font-medium text-muted-foreground">Include Patterns</div>
            <div className="space-y-1 mt-1">
              {config.patterns.map((pattern, idx) => (
                <div key={idx} className="text-sm font-mono bg-muted px-2 py-1 rounded">
                  {pattern}
                </div>
              ))}
            </div>
          </div>
          {config.exclude_patterns && config.exclude_patterns.length > 0 && (
            <div>
              <div className="text-sm font-medium text-muted-foreground">Exclude Patterns</div>
              <div className="space-y-1 mt-1">
                {config.exclude_patterns.map((pattern, idx) => (
                  <div key={idx} className="text-sm font-mono bg-muted px-2 py-1 rounded">
                    {pattern}
                  </div>
                ))}
              </div>
            </div>
          )}
          {config.case_sensitive !== undefined && (
            <div className="text-sm">
              <span className="text-muted-foreground">Case Sensitive: </span>
              <span className="font-medium">{config.case_sensitive ? 'Yes' : 'No'}</span>
            </div>
          )}
        </div>
      );

    case 'source_id':
      return (
        <div>
          <div className="text-sm font-medium text-muted-foreground mb-1">Source IDs</div>
          <div className="flex flex-wrap gap-2">
            {config.source_ids.map((id) => (
              <Badge key={id} variant="outline">
                {id}
              </Badge>
            ))}
          </div>
        </div>
      );

    case 'dataset_id':
      return (
        <div>
          <div className="text-sm font-medium text-muted-foreground mb-1">Dataset IDs</div>
          <div className="flex flex-wrap gap-2">
            {config.dataset_ids.map((id) => (
              <Badge key={id} variant="outline">
                {id}
              </Badge>
            ))}
          </div>
        </div>
      );

    case 'metadata':
      return (
        <div className="space-y-2">
          <div className="text-sm">
            <span className="text-muted-foreground">Field: </span>
            <span className="font-mono font-medium">{config.field_path}</span>
          </div>
          <div className="text-sm">
            <span className="text-muted-foreground">Operator: </span>
            <Badge variant="outline">{config.operator}</Badge>
          </div>
          <div className="text-sm">
            <span className="text-muted-foreground">Value: </span>
            <span className="font-medium">{String(config.value)}</span>
          </div>
        </div>
      );

    case 'date_range':
      return (
        <div className="space-y-2">
          <div className="text-sm">
            <span className="text-muted-foreground">Date Field: </span>
            <span className="font-mono font-medium">{config.date_field}</span>
          </div>
          {config.start_date && (
            <div className="text-sm">
              <span className="text-muted-foreground">Start Date: </span>
              <span className="font-medium">{config.start_date}</span>
            </div>
          )}
          {config.end_date && (
            <div className="text-sm">
              <span className="text-muted-foreground">End Date: </span>
              <span className="font-medium">{config.end_date}</span>
            </div>
          )}
        </div>
      );

    case 'file_size':
      return (
        <div className="space-y-2">
          {config.min_size_bytes !== undefined && (
            <div className="text-sm">
              <span className="text-muted-foreground">Minimum Size: </span>
              <span className="font-medium">{(config.min_size_bytes / 1024 / 1024).toFixed(2)} MB</span>
            </div>
          )}
          {config.max_size_bytes !== undefined && (
            <div className="text-sm">
              <span className="text-muted-foreground">Maximum Size: </span>
              <span className="font-medium">{(config.max_size_bytes / 1024 / 1024).toFixed(2)} MB</span>
            </div>
          )}
        </div>
      );

    case 'tag':
      return (
        <div className="space-y-2">
          <div>
            <div className="text-sm font-medium text-muted-foreground mb-1">Tags</div>
            <div className="flex flex-wrap gap-2">
              {config.tags.map((tag) => (
                <Badge key={tag} variant="outline">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
          {config.match_all !== undefined && (
            <div className="text-sm">
              <span className="text-muted-foreground">Match Mode: </span>
              <span className="font-medium">{config.match_all ? 'All Tags' : 'Any Tag'}</span>
            </div>
          )}
        </div>
      );

    default:
      return <div className="text-sm text-muted-foreground">Unknown rule type</div>;
  }
};

/**
 * RulesTab Component
 */
export const RulesTab: React.FC<RulesTabProps> = ({ view }) => {
  if (!view.rules || view.rules.length === 0) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center text-muted-foreground">
            <Filter className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium mb-2">No Rules Configured</p>
            <p className="text-sm">This view has no file selection rules</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Rule Logic */}
      <Card>
        <CardHeader>
          <CardTitle>Rule Logic</CardTitle>
          <CardDescription>
            How multiple rules are combined: <Badge variant="outline">{view.rule_logic}</Badge>
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Rules List */}
      <div className="space-y-4">
        {view.rules.map((rule, index) => {
          const Icon = getRuleIcon(rule.type);
          const typeLabel = getRuleTypeLabel(rule.type);
          const badgeColor = getRuleTypeBadge(rule.type);

          return (
            <Card key={rule.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-muted rounded-lg">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Rule {index + 1}</CardTitle>
                      <CardDescription>
                        <Badge variant="outline" className={badgeColor}>
                          {typeLabel}
                        </Badge>
                      </CardDescription>
                    </div>
                  </div>
                  {index < view.rules.length - 1 && (
                    <Badge variant="secondary">{view.rule_logic}</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <RuleConfigDetails config={rule.config} />
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
